import { Badge, HStack, Input, Stack, Text } from "@chakra-ui/react";
import * as React from "react";
import { LuChartNoAxesColumn, LuSearch, LuTarget } from "react-icons/lu";
import {
  Link as RouterLink,
  useParams,
  useSearchParams,
} from "react-router-dom";

import { GoogleAdsCampaignRow, getGoogleAdsCampaigns } from "../api/google-ads";
import { useBreadcrumbs } from "../components/breadcrumbs";
import { Button } from "../components/button";
import DataTable from "../components/data-table";
import { EmptyState } from "../components/empty-state";
import IconCircle from "../components/icon-circle";
import { InputGroup } from "../components/input-group";
import { MetricCard, MetricGroup } from "../components/metric-card";
import Page from "../components/page";
import PageBody from "../components/page-body";
import PageHeader from "../components/page-header";
import {
  SegmentedControl,
  SegmentedControlItem,
} from "../components/segmented-control";
import { useAppSelector } from "../hooks";
import { useGoogleAdsAccountName } from "../hooks/google-ads-account-name";
import { useGoogleAdsLoginCustomerId } from "../hooks/google-ads-login-customer-id";
import { useLoading } from "../hooks/loading";
import { useToaster } from "../hooks/toaster";
import { useWorkspaceURL } from "../hooks/workspace-url";
import {
  CampaignTab,
  isPausedInGoogle,
  isSupportedGoogleCampaign,
  matchesCampaignTab,
} from "../lib/keyword-agent-status";

const CAMPAIGN_TABS: SegmentedControlItem[] = [
  { value: "all", label: "All campaigns" },
  { value: "active", label: "Active" },
  { value: "needs-review", label: "Needs review" },
];

const DEFAULT_TAB: CampaignTab = "all";

const tabValue = (value: string | null): CampaignTab =>
  CAMPAIGN_TABS.some((tab) => tab.value === value)
    ? (value as CampaignTab)
    : DEFAULT_TAB;

// A campaign with no agent on any of its ad groups has nothing to show in
// these columns, and a zero would read as "the agent found nothing".
const isCovered = (campaign: GoogleAdsCampaignRow) =>
  campaign["ad-groups"]["with-agent"] > 0;

const orDash = (campaign: GoogleAdsCampaignRow, value: number) =>
  isCovered(campaign) && value > 0 ? value.toLocaleString() : "—";

export default function GoogleCampaignsPage() {
  const { customerId } = useParams();
  const { loginCustomerId, withLoginCustomerId } =
    useGoogleAdsLoginCustomerId();
  const workspaceSlug = useAppSelector((state) => state.workspace.slug);
  const workspaceURL = useWorkspaceURL();
  const [searchParams, setSearchParams] = useSearchParams();
  const withToaster = useToaster();

  const [campaigns, setCampaigns] = React.useState<GoogleAdsCampaignRow[]>([]);
  const [isLoading, withLoading] = useLoading();
  const [hasLoadedOnce, setHasLoadedOnce] = React.useState(false);

  const accountName = useGoogleAdsAccountName(workspaceSlug, customerId);

  useBreadcrumbs([
    { label: "Dashboard", to: workspaceURL("") },
    { label: "Google", to: workspaceURL("google-ads-accounts") },
    { label: accountName },
  ]);

  const searchQuery = searchParams.get("search") || "";
  const tab = tabValue(searchParams.get("tab"));

  const loadCampaigns = React.useCallback(async () => {
    if (!workspaceSlug || !customerId) return;
    await withLoading(async () => {
      await withToaster(async () => {
        setCampaigns(
          await getGoogleAdsCampaigns(
            workspaceSlug,
            customerId,
            loginCustomerId,
          ),
        );
        setHasLoadedOnce(true);
      });
    });
  }, [customerId, loginCustomerId, withLoading, withToaster, workspaceSlug]);

  React.useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const setParam = (key: string, value: string | null) => {
    setSearchParams((prev) => {
      const params = new URLSearchParams(prev);
      if (value) {
        params.set(key, value);
      } else {
        params.delete(key);
      }
      return params;
    });
  };

  const adGroupsURL = (campaignId: number) =>
    withLoginCustomerId(
      workspaceURL(
        `google-ads-accounts/${customerId}/campaigns/${campaignId}/ad-groups`,
      ),
    );

  const filtered = React.useMemo(
    () =>
      campaigns.filter((campaign) => {
        if (!matchesCampaignTab(campaign, tab)) {
          return false;
        }
        return (
          !searchQuery ||
          campaign.name.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }),
    [campaigns, searchQuery, tab],
  );

  // Account-level totals, so they read the whole list rather than the
  // filtered view.
  const totals = React.useMemo(
    () =>
      campaigns.reduce(
        (sum, { stats }) => ({
          excluded: sum.excluded + stats.excluded,
          pendingReview: sum.pendingReview + stats["pending-review"],
          analyzed: sum.analyzed + stats.analyzed,
        }),
        { excluded: 0, pendingReview: 0, analyzed: 0 },
      ),
    [campaigns],
  );

  return (
    <Page loading={isLoading && !hasLoadedOnce}>
      <Stack flex="1" gap="6">
        <PageHeader
          title="Google"
          subtitle="Campaigns in your connected Google Ads account"
        >
          <Button asChild variant="surface">
            <RouterLink to={workspaceURL("conversion-actions/google")}>
              <LuChartNoAxesColumn size="16" />
              Manage conversions
            </RouterLink>
          </Button>
        </PageHeader>

        <PageBody>
          <MetricGroup>
            <MetricCard
              label="Negative Keywords Applied"
              value={totals.excluded.toLocaleString()}
            />
            <MetricCard
              label="Pending review"
              value={totals.pendingReview.toLocaleString()}
            />
            <MetricCard
              label="Search terms analyzed"
              value={totals.analyzed.toLocaleString()}
            />
          </MetricGroup>

          <Stack gap="4" align="flex-start">
            <InputGroup
              startElement={<LuSearch size="16" />}
              width={"324px" as any}
              maxWidth="full"
            >
              <Input
                placeholder="Search for a campaign"
                value={searchQuery}
                onChange={(e) => setParam("search", e.target.value || null)}
              />
            </InputGroup>
            <SegmentedControl
              items={CAMPAIGN_TABS}
              value={tab}
              onValueChange={(value) =>
                setParam("tab", value === DEFAULT_TAB ? null : value)
              }
            />
          </Stack>

          {filtered.length === 0 && !isLoading ? (
            <HStack flex="1" alignItems="center" paddingY="8">
              <EmptyState
                icon={<IconCircle Icon={LuTarget} size="md" variant="flat" />}
                title="No campaigns match this view"
                description={
                  campaigns.length === 0
                    ? "No campaigns found for this Google Ads account."
                    : "Try a different search or filter."
                }
              />
            </HStack>
          ) : (
            <DataTable
              data={filtered}
              skeleton={isLoading && hasLoadedOnce ? 3 : undefined}
              template="minmax(240px,1fr) 132px 148px 120px"
              minTableWidth="740px"
              pagination
              rowHref={(campaign) =>
                isSupportedGoogleCampaign(campaign)
                  ? adGroupsURL(campaign.id)
                  : null
              }
              columns={[
                {
                  name: "Campaign",
                  cell: ({ value: campaign }) => (
                    <HStack gap="1.5" minWidth="0">
                      <Text fontWeight="medium" truncate title={campaign.name}>
                        {campaign.name}
                      </Text>
                      {isPausedInGoogle(campaign) && (
                        <Badge flexShrink="0">Paused</Badge>
                      )}
                    </HStack>
                  ),
                },
                {
                  // The dash is for a campaign with no ad groups at all,
                  // which is a different thing from none being set up.
                  name: "Ad groups",
                  cell: ({ value: campaign }) => {
                    const { total, "with-agent": withAgent } =
                      campaign["ad-groups"];
                    return (
                      <Text color={withAgent > 0 ? undefined : "fg.muted"}>
                        {total > 0 ? `${withAgent} of ${total}` : "—"}
                      </Text>
                    );
                  },
                },
                {
                  name: "Pending review",
                  align: "end",
                  cell: ({ value: campaign }) => (
                    <Text>
                      {orDash(campaign, campaign.stats["pending-review"])}
                    </Text>
                  ),
                },
                {
                  name: "Excluded",
                  align: "end",
                  cell: ({ value: campaign }) => (
                    <Text>{orDash(campaign, campaign.stats.excluded)}</Text>
                  ),
                },
              ]}
            />
          )}
        </PageBody>
      </Stack>
    </Page>
  );
}
